import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { useAgent } from '@/lib/api/agents';
import { ApiError } from '@/lib/api/client';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';
import { useWorkspacePath } from '@/lib/workspacePath';

/**
 * Context handed to the agent sub-pages (spec 0013, ADR-0002): the agent is
 * resolved ONCE here from the URL slug, and its UUID drives every nested/action
 * call (chat, sessions, connectors, catalogs, email), while `agentRef` (the URL
 * segment, normally the slug) rebuilds readable in-app links.
 */
export interface AgentOutletContext {
  agentId: string;
  agentRef: string;
}

export function useAgentContext(): AgentOutletContext {
  return useOutletContext<AgentOutletContext>();
}

/**
 * Shell for a single agent (spec 0012): the agent header plus a tab bar that
 * switches between the Configuration (index), Chat, and History pages, rendered
 * through an <Outlet/>. It owns the agent not-found state so all three tabs fail
 * the same way. The URL addresses the agent by slug (spec 0013), but a UUID in
 * the segment still resolves (AC-9). Supersedes the single crowded chat screen
 * (0009 ADR-0003 -> 0012 ADR-0001).
 */
export function AgentLayout() {
  const { agentRef } = useParams<{ agentRef: string }>();
  const ws = useWorkspacePath();
  const agent = useAgent(agentRef);

  if (agent.isLoading) {
    return (
      <div className="py-10">
        <Spinner label="Loading agent" />
      </div>
    );
  }

  if (agent.error || !agent.data) {
    const notFound = agent.error instanceof ApiError && agent.error.status === 404;
    return (
      <div
        role="alert"
        data-testid="agent-not-found"
        className="rounded-md border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700"
      >
        <p>{notFound ? 'Agent not found.' : 'Failed to load this agent.'}</p>
        <Link
          to={ws('agents')}
          className="mt-2 inline-block font-medium text-brand-700 hover:underline"
        >
          Back to agents
        </Link>
      </div>
    );
  }

  const ref = agentRef ?? agent.data.id;
  const context: AgentOutletContext = { agentId: agent.data.id, agentRef: ref };

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'rounded-md px-3 py-2 text-sm font-medium transition-colors motion-reduce:transition-none',
      isActive
        ? 'bg-brand-50 text-brand-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
    );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex shrink-0 items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-gray-900">{agent.data.name}</h1>
          {agent.data.model_display_name && (
            <p className="truncate text-sm text-gray-500">{agent.data.model_display_name}</p>
          )}
        </div>
        <Link to={ws('agents')} className="shrink-0 text-sm text-brand-600 hover:underline">
          Back to agents
        </Link>
      </div>

      <nav
        aria-label="Agent sections"
        className="flex shrink-0 gap-1 border-b border-gray-200 pb-2"
      >
        <NavLink to={ws('agents', ref, 'chat')} data-testid="agent-tab-chat" className={tabClass}>
          Chat
        </NavLink>
        <NavLink
          to={ws('agents', ref, 'history')}
          data-testid="agent-tab-history"
          className={tabClass}
        >
          History
        </NavLink>
        <NavLink end to={ws('agents', ref)} data-testid="agent-tab-config" className={tabClass}>
          Configuration
        </NavLink>
      </nav>

      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet context={context} />
      </div>
    </div>
  );
}
